// SPDX-License-Identifier: AGPL-3.0-or-later

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {makeAutoObservable, runInAction} from 'mobx';

export type VoiceOverlayAvatarSize = 'small' | 'medium' | 'large';
export type VoiceOverlayVisibility = 'always' | 'speaking' | 'never';

const MAX_USERS = 25;

function clampMaxUsers(value: number): number {
	if (!Number.isFinite(value)) return 4;
	return Math.max(0, Math.min(MAX_USERS, Math.round(value)));
}

class VoiceOverlaySettings {
	avatarSize: VoiceOverlayAvatarSize = 'large';
	showNames: VoiceOverlayVisibility = 'always';
	showUsers: VoiceOverlayVisibility = 'always';
	maxUsers = 4;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.initPersistence();
	}

	private async initPersistence(): Promise<void> {
		await makePersistent(this, 'VoiceOverlaySettings', ['avatarSize', 'showNames', 'showUsers', 'maxUsers']);
		runInAction(() => {
			this.avatarSize = this.normalizeSize(this.avatarSize);
			this.showNames = this.normalizeVisibility(this.showNames);
			this.showUsers = this.normalizeVisibility(this.showUsers);
			this.maxUsers = clampMaxUsers(this.maxUsers);
		});
	}

	private normalizeSize(value: string): VoiceOverlayAvatarSize {
		if (value === 'small' || value === 'medium' || value === 'large') return value;
		return 'large';
	}

	private normalizeVisibility(value: string): VoiceOverlayVisibility {
		if (value === 'always' || value === 'speaking' || value === 'never') return value;
		return 'always';
	}

	setAvatarSize(value: VoiceOverlayAvatarSize): void {
		this.avatarSize = value;
	}

	setShowNames(value: VoiceOverlayVisibility): void {
		this.showNames = value;
	}

	setShowUsers(value: VoiceOverlayVisibility): void {
		this.showUsers = value;
	}

	setMaxUsers(value: number): void {
		this.maxUsers = clampMaxUsers(value);
	}
}

export default new VoiceOverlaySettings();
